import { useMutation } from '@tanstack/react-query';
import { Button, Dropdown, DropdownOption, Modal, TextField } from '@volvo/vcdk-react';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { IWifiConfigurationRequest, IWifiNetworkRow } from '@/service/vpc/types/IWifiConfiguration';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { vpcService } from '@/service/vpc/vpcService';

type EditNetworkFormFields = {
  priority: number;
  password: string;
};

type Props = {
  chassisId: string;
  row: IWifiNetworkRow | null;
  existingNetworks: IWifiNetworkRow[];
  onClose: () => void;
  onMutationSuccess: (correlationId: string, updatedNetworks: IWifiNetworkRow[]) => void;
};

type EditNetworkModalContentProps = {
  chassisId: string;
  row: IWifiNetworkRow;
  existingNetworks: IWifiNetworkRow[];
  onClose: () => void;
  onMutationSuccess: (correlationId: string, updatedNetworks: IWifiNetworkRow[]) => void;
};

const EditNetworkModalContent: React.FC<EditNetworkModalContentProps> = ({
  chassisId,
  row,
  existingNetworks,
  onClose,
  onMutationSuccess,
}) => {
  const { t } = useTranslation();
  // Save button lives in the modal's footer slot, outside the <form>, so submission is triggered imperatively via this ref
  const [formEl, setFormEl] = useState<HTMLFormElement | null>(null);
  // Tracks the current (potentially swapped) priority for every network
  const [networkPriorities, setNetworkPriorities] = useState<Map<string, number>>(
    new Map(existingNetworks.map((n) => [n.ssid, n.priority]))
  );

  const ssid = row?.ssid ?? '';

  const { handleSubmit, control } = useForm<EditNetworkFormFields>({
    defaultValues: {
      priority: row?.priority ?? 1,
      password: '',
    },
  });

  const { mutate: updateWifiConfiguration, isPending } = useMutation({
    mutationFn: ({ chassisId, configurations }: { chassisId: string; configurations: IWifiConfigurationRequest[] }) => {
      return vpcService.Services.updateWifiConfiguration({ chassisId, configurations });
    },
  });

  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handlePriorityChange = (onChange: (value: number) => void, newPriority: number) => {
    const updated = new Map(networkPriorities);
    // If another network already has this priority, swap it with the current network's priority
    const conflict = [...updated.entries()].find(([otherSsid, p]) => otherSsid !== ssid && p === newPriority);
    if (conflict) {
      updated.set(conflict[0], updated.get(ssid)!);
    }
    updated.set(ssid, newPriority);
    setNetworkPriorities(updated);
    onChange(newPriority);
  };

  const handleOnSave = useCallback(
    (formdata: EditNetworkFormFields) => {
      // Send all networks; only include password for the one being edited
      const configurations: IWifiConfigurationRequest[] = existingNetworks.map((n) => ({
        ssid: n.ssid,
        priority: networkPriorities.get(n.ssid) ?? n.priority,
        ...(n.ssid === ssid ? { password: formdata.password } : {}),
      }));

      updateWifiConfiguration(
        { chassisId, configurations },
        {
          // Only close the modal and notify the parent once the mutation actually succeeds
          onSuccess: (result) => {
            const updatedNetworks: IWifiNetworkRow[] = existingNetworks
              .map((n) => ({ ...n, priority: networkPriorities.get(n.ssid) ?? n.priority }))
              .sort((a, b) => a.priority - b.priority);

            handleOnClose();
            onMutationSuccess(result.correlationId, updatedNetworks);
          },
        }
      );
    },
    [chassisId, existingNetworks, networkPriorities, ssid, handleOnClose, onMutationSuccess, updateWifiConfiguration]
  );

  const onSubmit: SubmitHandler<EditNetworkFormFields> = useCallback(
    (formdata) => {
      handleOnSave(formdata);
    },
    [handleOnSave]
  );

  return createPortal(
    <Modal open={!!row} onClose={handleOnClose} maxWidth="90%" width={700}>
      <span slot="header">{t(($) => $.components.modal.add_network_modal.edit_network)}</span>
      <form ref={setFormEl} className="flex flex-col gap-8" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="priority"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Dropdown
              label={t(($) => $.components.modal.add_network_modal.priority)}
              size="medium"
              className="w-full"
              value={String(field.value)}
              placement="bottom"
              onChange={(e) => handlePriorityChange(field.onChange, Number(e.detail.value))}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <DropdownOption key={n} value={String(n)}>
                  {String(n)}
                </DropdownOption>
              ))}
            </Dropdown>
          )}
        />
        <TextField label={t(($) => $.components.modal.add_network_modal.network_name)} value={ssid} disabled />
        <Controller
          name="password"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              required
              type="password"
              label={t(($) => $.components.modal.add_network_modal.password)}
              helperText={t(($) => $.components.modal.add_network_modal.password_helperText)}
              value={field.value}
              onInput={(e) => field.onChange(e.detail.value)}
            />
          )}
        />
      </form>
      <span slot="modalFooter">
        <div className="flex gap-4">
          <Button variant="secondary" type="button" onClick={handleOnClose} disabled={isPending}>
            {t(($) => $.common.cancel)}
          </Button>
          <Button variant="primary" type="button" onClick={() => formEl?.requestSubmit()} disabled={isPending}>
            {t(($) => $.common.save)}
          </Button>
        </div>
      </span>
    </Modal>,
    document.body
  );
};

const EditNetworkModal: React.FC<Props> = ({ chassisId, row, existingNetworks, onClose, onMutationSuccess }) => {
  if (!row) return null;

  // Key forces remount when modal context changes, so local state re-initializes from props without effects.
  const modalKey = `${row.ssid}-${row.priority}`;

  return (
    <EditNetworkModalContent
      key={modalKey}
      chassisId={chassisId}
      row={row}
      existingNetworks={existingNetworks}
      onClose={onClose}
      onMutationSuccess={onMutationSuccess}
    />
  );
};

export default withComponentErrorBoundary(EditNetworkModal);
